/**
 * Pure helper functions for deriving report data from AI verification data.
 *
 * These replace the inline derivations that previously lived in the application
 * detail component, and they key off the *selected* applicant's verification
 * data so that switching applicants in the UI always shows the correct values.
 */

import { formatCurrency } from '#app/utils/misc'

// ---------------------------------------------------------------------------
// Shared types
// ---------------------------------------------------------------------------

export interface VerificationDataItem {
	date: string
	amount: number
	holder: string
	category: string
	currency: string
	description: string
}

export interface CollectionItem {
	creditor: string
	date: string
	amount: number
	originalAmount: number
	currency: string
	status: string
	remarks: string
}

export interface BankruptcyItem {
	reported: string
	trustee: string
	assets: number
	liability: number
	currency: string
	status: string
	remarks: string
}

export interface AIReportPredictions {
	applicant?: {
		name?: string
		trueTenantScore?: number
		rentRange?: { min: number; max: number; currency: string }
		guarantorRecommendation?: string
		guarantorScore?: number
	}
	guarantor?: { name?: string }
	guarantorAccessibility?: { status?: string; reason?: string }
	financialSummary?: {
		savingsInvestments?: number
		currency?: string
	}
	debtSummary?: {
		monthlyDebtPayment?: number
		totalDebtOwing?: number
		details?: Array<{
			type: string
			used: string
			balance: number
			limit: number
			monthlyPayments: number
			pastDue: number
			paymentStatus: string
		}>
	}
	collections?: {
		data: CollectionItem[]
	}
	/**
	 * Written-off / charge-off accounts. The upstream payload key is
	 * `writtenOffAccounts` (NOT `writeOffs`); same row shape as collections.
	 * Absent/empty → the v2 detail card presence-gates to hidden.
	 */
	writtenOffAccounts?: {
		data: CollectionItem[]
	}
	bankruptcyAndInsolvency?: {
		data: BankruptcyItem[]
	}
	/**
	 * AI-generated narrative paragraph — PENDING upstream payload extension
	 * (FULLSCREEN-DATA-MAP §3): absent from every current payload. Modeled so
	 * the v2 detail page can presence-gate the AI Generated Summary card.
	 */
	summary?: string
	riskFlags?: {
		red?: Array<{ level: string; message: string }>
		yellow?: Array<{ level: string; message: string }>
	}
	verificationSummary?: {
		idVerification?: { status: string; details: string; data?: unknown }
		incomeVerification?: {
			status: string
			details: string
			data?: VerificationDataItem[]
		}
		rentVerification?: {
			status: string
			details: string
			data?: VerificationDataItem[]
		}
	}
	spendingCategories?: Record<string, { percentage: number; amount?: number }>
	monthlyExpenses?: Array<{
		category: string
		amount: number
		percentage: number
	}>
	expenseBreakdown?: Array<{
		name: string
		value: number
		percentage: number
	}>
}

export interface ReportData {
	applicantName: string
	pets: number
	children: number
	childrenDetails?: Array<{ name: string; age: number }>
	petsDetails?: Array<{
		petType: string
		breed: string
		weight: number | string
	}>
	score: number
	rentRange: string
	moveInDate: string
	flexibleMoveIn?: string | null
	recommendations: string[]
	verificationStatus: {
		id: boolean
		income: boolean
		rent: boolean
	}
	verificationDetails?: {
		id: string
		income: string
		rent: string
	}
	flaggedIssues: Array<{
		type: 'error' | 'warning'
		message: string
	}>
	totalSavings: string
	guarantorRecommendation?: {
		required: boolean
		reason: string
		score?: string
	}
	guarantorScore?: number | null
	guarantorStrength?: { summary: string | null }
	guarantorAccessibility?: { status?: string; reason?: string }
	imageUrl?: string
}

export interface DebtSummaryData {
	monthlyDebtPayment?: number
	totalDebtOwing?: number
	details?: Array<{
		type: string
		used: string
		balance: number
		limit: number
		monthlyPayments: number
		pastDue: number
		paymentStatus: string
	}>
	collections?: CollectionItem[]
	/** Written-off / charge-off accounts (predictions.writtenOffAccounts). */
	writtenOffAccounts?: CollectionItem[]
	bankruptcyAndInsolvency?: BankruptcyItem[]
}

export interface RiskFlagsData {
	red?: Array<{ level: string; message: string }>
	yellow?: Array<{ level: string; message: string }>
}

export interface VerificationSummaryData {
	idVerification?: { status: string; details?: string }
	incomeVerification?: {
		status: string
		details?: string
		data?: VerificationDataItem[]
	}
	rentVerification?: {
		status: string
		details?: string
		data?: VerificationDataItem[]
	}
	openRoomRecords?: {
		status: string
		url?: string
		records?: Array<{
			url: string
			name?: string
			title?: string
			address?: string
			issuedDate?: string
		}>
	}
	creditReport?: { status: string; data?: unknown }
}

export type ExpenseItem = { name: string; value: number; percentage: number }

// ---------------------------------------------------------------------------
// Internal helper: extract AI predictions from verificationData
// ---------------------------------------------------------------------------

export function extractPredictions(
	verificationData: unknown,
): AIReportPredictions | null {
	if (!verificationData || typeof verificationData !== 'object') return null
	try {
		const aiData = verificationData as { predictions?: AIReportPredictions }
		return aiData.predictions ?? null
	} catch {
		return null
	}
}

// ---------------------------------------------------------------------------
// 1. deriveReportData
// ---------------------------------------------------------------------------

/**
 * Derive the main report data from AI predictions.
 * Falls back to `null` if no predictions are available.
 */
export function deriveReportData(
	verificationData: unknown,
	applicantName: string,
	overallScore?: number | null,
	guarantorRequired?: boolean,
): ReportData | null {
	const predictions = extractPredictions(verificationData)
	if (!predictions) return null

	const isGuarantorResponse = !predictions.applicant && !!predictions.guarantor
	const applicant = (predictions.applicant || {}) as NonNullable<
		AIReportPredictions['applicant']
	>
	const guarantor = predictions.guarantor
	const riskFlags = predictions.riskFlags || {}
	const verificationSummary = predictions.verificationSummary || {}
	const financialSummary = predictions.financialSummary || {}

	const guarantorScore =
		typeof applicant.guarantorScore === 'number'
			? applicant.guarantorScore
			: null

	const resolvedName = isGuarantorResponse
		? guarantor?.name || applicantName
		: applicant.name || applicantName

	return {
		applicantName: resolvedName,
		pets: 0,
		children: 0,
		childrenDetails: undefined,
		petsDetails: undefined,
		score: isGuarantorResponse
			? 0
			: applicant.trueTenantScore || overallScore || 0,
		rentRange: isGuarantorResponse
			? '-'
			: applicant.rentRange
				? `${formatCurrency(applicant.rentRange.min)} - ${formatCurrency(applicant.rentRange.max)}`
				: '-',
		moveInDate: '-',
		flexibleMoveIn: null,
		recommendations: [],
		verificationStatus: {
			id: verificationSummary.idVerification?.status === 'Completed',
			income: verificationSummary.incomeVerification?.status === 'Completed',
			rent: false,
		},
		verificationDetails: {
			id: verificationSummary.idVerification?.details || '',
			income: verificationSummary.incomeVerification?.details || '',
			rent: '',
		},
		flaggedIssues: [
			...(riskFlags.red?.map((flag) => ({
				type: 'error' as const,
				message: `${flag.level}: ${flag.message}`,
			})) || []),
			...(riskFlags.yellow?.map((flag) => ({
				type: 'warning' as const,
				message: `${flag.level}: ${flag.message}`,
			})) || []),
		],
		totalSavings: financialSummary.savingsInvestments
			? `$${financialSummary.savingsInvestments.toLocaleString()} ${financialSummary.currency || 'CAD'}`
			: '-',
		guarantorRecommendation: isGuarantorResponse
			? undefined
			: {
					required:
						applicant.guarantorRecommendation
							?.toLowerCase()
							.includes('recommended') ||
						applicant.guarantorRecommendation
							?.toLowerCase()
							.includes('required') ||
						guarantorRequired ||
						false,
					reason: applicant.guarantorRecommendation || '',
				},
		guarantorStrength: undefined,
		guarantorScore,
		guarantorAccessibility: predictions.guarantorAccessibility,
		imageUrl: undefined,
	}
}

// ---------------------------------------------------------------------------
// 2. deriveDebtSummary
// ---------------------------------------------------------------------------

export function deriveDebtSummary(
	verificationData: unknown,
): DebtSummaryData | undefined {
	const predictions = extractPredictions(verificationData)
	if (!predictions?.debtSummary) return undefined

	return {
		...predictions.debtSummary,
		collections: predictions.collections?.data,
		writtenOffAccounts: predictions.writtenOffAccounts?.data,
		bankruptcyAndInsolvency: predictions.bankruptcyAndInsolvency?.data,
	}
}

// ---------------------------------------------------------------------------
// 3. deriveRiskFlags
// ---------------------------------------------------------------------------

export function deriveRiskFlags(
	verificationData: unknown,
): RiskFlagsData | undefined {
	const predictions = extractPredictions(verificationData)
	return predictions?.riskFlags ?? undefined
}

// ---------------------------------------------------------------------------
// 4. deriveVerificationSummary
// ---------------------------------------------------------------------------

export function deriveVerificationSummary(
	verificationData: unknown,
	creditReportData: unknown | null,
): VerificationSummaryData {
	const predictions = extractPredictions(verificationData)
	const vs = predictions?.verificationSummary

	return {
		idVerification: vs?.idVerification
			? {
					status: vs.idVerification.status,
					details: vs.idVerification.details,
				}
			: undefined,
		incomeVerification: vs?.incomeVerification
			? {
					status: vs.incomeVerification.status,
					details: vs.incomeVerification.details,
					data: vs.incomeVerification.data,
				}
			: undefined,
		rentVerification: vs?.rentVerification
			? {
					status: vs.rentVerification.status,
					details: vs.rentVerification.details,
					data: vs.rentVerification.data,
				}
			: undefined,
		openRoomRecords: undefined,
		creditReport: creditReportData
			? { status: 'Available', data: creditReportData }
			: undefined,
	}
}

// ---------------------------------------------------------------------------
// 5. deriveMonthlyExpenses
// ---------------------------------------------------------------------------

const SPENDING_CATEGORY_LABELS: Record<string, string> = {
	RecurringDebt: 'Recurring Debt',
	HouseObligations: 'House Obligations',
	DisposableSpending: 'Disposable Spending',
}

function spendingCategoryLabel(key: string): string {
	return (
		SPENDING_CATEGORY_LABELS[key] ??
		key
			.replace(/([A-Z])/g, ' $1')
			.replace(/^./, (s) => s.toUpperCase())
			.trim()
	)
}

function takeTop3(items: ExpenseItem[]): ExpenseItem[] {
	return [...items].sort((a, b) => b.percentage - a.percentage).slice(0, 3)
}

export function deriveMonthlyExpenses(
	verificationData: unknown,
	monthlyRent: number,
	effectiveAnnualIncome: number,
	debtSummary: DebtSummaryData | undefined,
): ExpenseItem[] {
	const predictions = extractPredictions(verificationData)

	// Priority 1: predictions.spendingCategories
	if (
		predictions?.spendingCategories &&
		typeof predictions.spendingCategories === 'object'
	) {
		const categories = Object.entries(predictions.spendingCategories).map(
			([key, catData]) => ({
				name: spendingCategoryLabel(key),
				value: typeof catData?.amount === 'number' ? catData.amount : 0,
				percentage:
					typeof catData?.percentage === 'number' ? catData.percentage : 0,
			}),
		)
		if (categories.length > 0) return takeTop3(categories)
	}

	// Priority 2: predictions.monthlyExpenses
	if (predictions?.monthlyExpenses && predictions.monthlyExpenses.length > 0) {
		return takeTop3(
			predictions.monthlyExpenses.map((exp) => ({
				name: exp.category,
				value: exp.amount,
				percentage: exp.percentage,
			})),
		)
	}

	// Priority 3: predictions.expenseBreakdown
	if (
		predictions?.expenseBreakdown &&
		predictions.expenseBreakdown.length > 0
	) {
		return takeTop3(predictions.expenseBreakdown)
	}

	// Fallback: Calculate from available data
	const rentAmount = monthlyRent ? Number.parseFloat(String(monthlyRent)) : 0
	const monthlyDebtPayment = debtSummary?.monthlyDebtPayment || 0
	const monthlyIncome = effectiveAnnualIncome / 12

	if (monthlyIncome > 0) {
		const expenses: ExpenseItem[] = []

		if (rentAmount > 0) {
			expenses.push({
				name: 'Rent / Housing',
				value: rentAmount,
				percentage: (rentAmount / monthlyIncome) * 100,
			})
		}

		if (monthlyDebtPayment > 0) {
			expenses.push({
				name: 'Debt Payments',
				value: monthlyDebtPayment,
				percentage: (monthlyDebtPayment / monthlyIncome) * 100,
			})
		}

		const remainingIncome = monthlyIncome - rentAmount - monthlyDebtPayment
		if (remainingIncome > 0) {
			const foodAmount = remainingIncome * 0.12
			expenses.push({
				name: 'Food & Groceries',
				value: foodAmount,
				percentage: (foodAmount / monthlyIncome) * 100,
			})

			const transportationAmount = remainingIncome * 0.1
			expenses.push({
				name: 'Transportation',
				value: transportationAmount,
				percentage: (transportationAmount / monthlyIncome) * 100,
			})

			const utilitiesAmount = remainingIncome * 0.08
			expenses.push({
				name: 'Utilities & Bills',
				value: utilitiesAmount,
				percentage: (utilitiesAmount / monthlyIncome) * 100,
			})

			const discretionaryAmount =
				remainingIncome - foodAmount - transportationAmount - utilitiesAmount
			if (discretionaryAmount > 0) {
				expenses.push({
					name: 'Discretionary Spending',
					value: discretionaryAmount,
					percentage: (discretionaryAmount / monthlyIncome) * 100,
				})
			}
		}

		const totalExpenses = expenses.reduce((sum, exp) => sum + exp.value, 0)
		if (totalExpenses > 0) {
			return takeTop3(
				expenses.map((exp) => ({
					...exp,
					percentage: (exp.value / totalExpenses) * 100,
				})),
			)
		}
	}

	// Fallback: just rent
	if (rentAmount > 0) {
		return takeTop3([
			{ name: 'Rent / Housing', value: rentAmount, percentage: 100 },
		])
	}

	return []
}

// Re-export the spending category label for use in components
export { spendingCategoryLabel as getSpendingCategoryLabel }

// ---------------------------------------------------------------------------
// Declared values for the income / rent verification PDFs
// ---------------------------------------------------------------------------

export interface DeclaredVerificationValues {
	currentAnnualIncome: number | null
	previousAnnualIncome: number | null
	declaredRent: number | null
	previousDeclaredRent: number | null
	rentalStatus: string | null
}

/**
 * Derive the applicant-declared income and rent figures shown on the
 * verification PDFs from raw screening step data.
 *
 * The employment step stores flat, prefix-named fields (`annualIncome`,
 * `previousAnnualIncome`, `noHistoryAnnualIncome`, …) — there is no nested
 * `previousEmployment` object. Reads are gated on `employmentStatus` the same
 * way the application-detail page gates its display, so values from a
 * non-selected status section never leak into the report. Step data saved
 * before the status field existed has no `employmentStatus`; those fall back
 * to the ungated flat reads.
 */
export function deriveDeclaredVerificationValues(stepData: {
	employment?: Record<string, unknown>
	rental?: Record<string, unknown>
}): DeclaredVerificationValues {
	// Step fields are free-text; coerce to numbers and discard junk.
	const toAmount = (raw: unknown): number | null => {
		if (typeof raw === 'number')
			return Number.isFinite(raw) && raw > 0 ? raw : null
		if (typeof raw !== 'string') return null
		const n = Number.parseFloat(raw.replace(/[^0-9.]/g, ''))
		return Number.isFinite(n) && n > 0 ? n : null
	}

	const employment = stepData.employment
	const rental = stepData.rental

	const status =
		typeof employment?.employmentStatus === 'string'
			? employment.employmentStatus
			: ''

	const sumWithAdditional = (): number | null => {
		const base = toAmount(employment?.annualIncome) ?? 0
		const additional = Array.isArray(employment?.additionalEmployments)
			? (employment.additionalEmployments as Array<Record<string, unknown>>)
					.map((e) => toAmount(e?.annualIncome) ?? 0)
					.reduce((sum, n) => sum + n, 0)
			: 0
		const total = base + additional
		return total > 0 ? total : null
	}

	let currentAnnualIncome: number | null = null
	let previousAnnualIncome: number | null = null
	if (status === 'currently-employed') {
		currentAnnualIncome = sumWithAdditional()
		previousAnnualIncome = toAmount(employment?.previousAnnualIncome)
	} else if (status === 'not-employed') {
		previousAnnualIncome = toAmount(employment?.previousAnnualIncome)
	} else if (status === 'no-employment-history') {
		currentAnnualIncome = toAmount(employment?.noHistoryAnnualIncome)
	} else {
		// Legacy / missing status: keep the ungated flat reads working.
		currentAnnualIncome = sumWithAdditional()
		previousAnnualIncome = toAmount(employment?.previousAnnualIncome)
	}

	return {
		currentAnnualIncome,
		previousAnnualIncome,
		declaredRent: toAmount(rental?.currentRent),
		previousDeclaredRent: toAmount(rental?.previousRent),
		rentalStatus:
			typeof rental?.rentalStatus === 'string' && rental.rentalStatus
				? rental.rentalStatus
				: null,
	}
}
